import React, { useMemo, useState, useCallback, useEffect } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import TextField from '@mui/material/TextField';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NumericFormat } from 'react-number-format';
import { usePersistedDataStore } from '../../store.js';
import { useUser } from '../../UserContext.js';
import { processSettlement, validateSettlementBatch, clampDisplayedSettlement } from './settlementService.js';
import {
  getDefaultSettlementStage,
  isScarStageAvailableForRows,
  resolveSettlementCaseItem,
} from './settlementStageUtils.js';
import { isCaseSettled, isMasterPetitionCase } from '../SummaryCaseTable/utils/helpers.js';
import { getConversionFactor } from '../../../utils/conversionUtils';
import { computeDirection, computeReductionPercent, applySettlementRounding } from './SettlementRounding.js';
import { getAVUtil, getIFMVUtil } from '../../../utils/getAVUtil';
import { createOfferContext } from '../../../utils/offerContextUtils.js';
import { getMostRecentOfferValue } from '../../../utils/mostRecentOfferUtils';
import { toast } from '../../../common/toast';
import logger from '../../../utils/logger';

const offerAmount = (row, offeredBy) => {
  const candidates = offeredBy === 'Town'
    ? [getMostRecentOfferValue(row?.Negotiations, 'Town'), row?.TownOffer]
    : [getMostRecentOfferValue(row?.Negotiations, 'Aventine'), row?.OfferValue];
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const n = typeof value === 'string' ? Number(String(value).replace(/,/g, '')) : Number(value);
    if (Number.isFinite(n) && n !== 0) return n;
  }
  return null;
};

const offerSourceAvailable = (rows, offeredBy) => {
  const targets = (rows || []).filter((row) => !isCaseSettled(row));
  return targets.length > 0 && targets.every((row) => offerAmount(row, offeredBy) !== null);
};

// each property level input for settlement. Inspired by the individual settlement popup.
const RowEditor = ({ row, rounding, stage, conversionFactor, defaultValue, isDefaultValueInMV = false, isSettled, onValidationError, onClearValidationError, initialIsAVMode = false, defaultFrom, mostRecentTownResponse, bulk, shouldFocus }) => {
  const [rawValue, setRawValue] = useState('');
  const [isAVMode, setIsAVMode] = useState(initialIsAVMode);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [customWithdrawalReason, setCustomWithdrawalReason] = useState('');
  const [withdrawalError, setWithdrawalError] = useState('');

  const rowRef = React.useRef(null);

  const bulkDisabled = !!bulk && !bulk.isFirst;

  // Get negotiationObj from persisted state
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);

  // Get the case object from negotiationObj.cases by matching parcel_id
  const caseObject = useMemo(() => {
    if (!negotiationObj?.cases || !row?.parcel_id) return null;
    return negotiationObj.cases.find(c => c.parcel_id === row.parcel_id) || null;
  }, [negotiationObj, row?.parcel_id]);


  useEffect(() => {
    setWithdrawalError(onValidationError ? String(onValidationError) : '');
  }, [onValidationError]);

  // When this row is the first offending entry, scroll it into view
  useEffect(() => {
    if (!shouldFocus || !rowRef.current) return;
    try {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {}
  }, [shouldFocus]);

  // Update withdrawal state based on defaultFrom and town response
  useEffect(() => {
    const shouldBeWithdrawal = defaultFrom === 'Town' && 
      mostRecentTownResponse && 
      mostRecentTownResponse.toLowerCase() === 'withdraw';
    
    setIsWithdrawal(shouldBeWithdrawal);
    
    // Clear withdrawal reason when switching away from town withdrawal
    if (!shouldBeWithdrawal) {
      setWithdrawalReason('');
      setCustomWithdrawalReason('');
      if (onClearValidationError) {
        onClearValidationError(row?.parcel_id);
      }
    }
  }, [defaultFrom, mostRecentTownResponse, row?.parcel_id, onClearValidationError]);

  // Withdrawal reason options
  const withdrawalReasons = [
    'Not owner occupied',
    'Income producing',
    'Duplicate filing',
    'Moratorium (Prior year reduction)',
    'Commercial',
    'Stip N/C',
    'Other'
  ];

  // Initialize from selected offer source (Aventine/Town)
  // defaultValue can be in AV or MV terms based on isDefaultValueInMV flag
  // Display as AV or MV based on isAVMode; nearest-dollar rounding only
  useEffect(() => {
    // Skip if withdrawal is active - withdrawal useEffect will handle the value
    if (isWithdrawal) {
      return;
    }

    if (isSettled) {
      const determinationValueAV = stage === 'SCAR'
        ? (row?.SCARDeterminationValue || 0)
        : stage === 'Art7'
          ? (row?.Art7DeterminationValue || 0)
          : (row?.BARDeterminationValue || 0);
      if (determinationValueAV > 0) {
        const init = isAVMode ? Math.round(determinationValueAV) : (conversionFactor > 0 ? Math.round(determinationValueAV / conversionFactor) : Math.round(determinationValueAV));
        setRawValue(String(init));
      } else {
        setRawValue('');
      }
      return;
    }

    if (defaultValue !== null && defaultValue !== undefined && defaultValue !== '') {
      const n = typeof defaultValue === 'string' ? parseFloat(defaultValue.replace(/,/g, '')) : defaultValue;
      
      if (!isNaN(n)) {
        let init;
        if (isDefaultValueInMV) {
          // Value is already in MV terms
          if (isAVMode) {
            // Displaying in AV mode, convert MV to AV
            init = conversionFactor > 0 ? Math.round(n * conversionFactor) : Math.round(n);
          } else {
            // Displaying in MV mode, use as-is
            init = Math.round(n);
          }
        } else {
          // Value is in AV terms
          if (isAVMode) {
            // Displaying in AV mode, use as-is
            init = Math.round(n);
          } else {
            // Displaying in MV mode, convert AV to MV
            init = conversionFactor > 0 ? Math.round(n / conversionFactor) : Math.round(n);
          }
        }
        
        setRawValue(String(init));
        return;
      }
    }

    setRawValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, isDefaultValueInMV, isSettled, stage, row, conversionFactor, isAVMode, isWithdrawal]);

  // Handle withdrawal logic
  useEffect(() => {
    if (isWithdrawal) {
      // Set value to IFMV if in MV mode, or convert IFMV to AV if in AV mode
      const ifmv = getIFMVUtil(caseObject);
      if (isAVMode) {
        // Convert IFMV to AV
        const avValue = Math.round(ifmv * conversionFactor);
        setRawValue(String(avValue));
      } else {
        // Use IFMV directly in MV mode
        setRawValue(String(Math.round(ifmv)));
      }
    } else {
      // When withdrawal is turned off, let defaultValue useEffect handle setting the value
      // This ensures the value updates when switching between Aventine and Town offers
    }
  }, [isWithdrawal, isAVMode, caseObject, conversionFactor]);

  // Bulk behaviour: non-first rows mirror only the action type (withdraw / no-change),
  // and compute their own values relative to their assessments.
  useEffect(() => {
    if (!bulk || bulk.isFirst || !bulk.type) return;
    if (bulk.type === 'withdrawal') {
      if (!isWithdrawal) setIsWithdrawal(true);
    } else if (bulk.type === 'no-change') {
      if (isWithdrawal) setIsWithdrawal(false);
      const assessmentAV = getAVUtil(caseObject);
      const ifmv = getIFMVUtil(caseObject);
      if (isAVMode && assessmentAV != null) {
        setRawValue(String(Math.round(assessmentAV)));
      } else if (!isAVMode && ifmv != null) {
        setRawValue(String(Math.round(ifmv)));
      }
    }
  }, [bulk, bulk?.type, bulk?.isFirst, isWithdrawal, isAVMode, caseObject]);

  // Non-first bulk rows mirror the first row's withdrawal reasons.
  useEffect(() => {
    if (!bulk || bulk.isFirst) return;
    if (typeof bulk.withdrawalReason === 'string') {
      setWithdrawalReason(bulk.withdrawalReason);
    }
    if (typeof bulk.customWithdrawalReason === 'string') {
      setCustomWithdrawalReason(bulk.customWithdrawalReason);
    }
  }, [bulk, bulk?.withdrawalReason, bulk?.customWithdrawalReason]);

  // First row publishes the bulk action type.
  useEffect(() => {
    if (!bulk || !bulk.isFirst || !bulk.setType) return;
    bulk.setType(isWithdrawal ? 'withdrawal' : 'no-change');
  }, [bulk, isWithdrawal]);

  const handleToggleMode = useCallback(() => {
    const n = rawValue === '' ? null : parseInt(String(rawValue).replace(/,/g, ''), 10);
    const nextIsAV = !isAVMode;
    if (n !== null && Number.isFinite(n)) {
      const converted = isAVMode
        ? Math.round(n / conversionFactor / 10) * 10
        : Math.round(n * conversionFactor);
      setRawValue(String(clampDisplayedSettlement(converted, nextIsAV, caseObject)));
    }
    setIsAVMode(nextIsAV);
  }, [isAVMode, rawValue, conversionFactor, caseObject]);

  const onSettlementAmountBlur = useCallback(() => {
    setRawValue((prev) => {
      const trimmed = String(prev ?? '').replace(/,/g, '').trim();
      if (trimmed === '') return '';
      const n = parseInt(trimmed, 10);
      if (!Number.isFinite(n)) return prev;
      return String(clampDisplayedSettlement(n, isAVMode, caseObject));
    });
  }, [isAVMode, caseObject]);


  const roundingInfo = useMemo(() => {
    const n = rawValue === '' ? null : parseInt(rawValue, 10);
    if (!n) return { direction: null, borderColor: '#ccc' };
    const propertyAssessment = getAVUtil(caseObject);
    const avFinal = applySettlementRounding(n, isAVMode, conversionFactor, rounding, propertyAssessment);
    if (avFinal === Number(propertyAssessment)) return { direction: null, borderColor: '#ccc' };
    const { percent } = computeReductionPercent(n, isAVMode, propertyAssessment, getIFMVUtil(caseObject));
    if (percent !== null && Math.abs(percent) < 0.005) {
      return { direction: null, borderColor: '#ccc' };
    }
    const direction = computeDirection(n, isAVMode, conversionFactor, avFinal);
    if (!direction) return { direction: null, borderColor: '#ccc' };
    return { direction, borderColor: direction === 'up' ? '#f44336' : '#4caf50' };
  }, [rawValue, rounding, isAVMode, conversionFactor, caseObject]);

  return (
      <Box ref={rowRef} sx={{ maxWidth: 1400, width: '100%', m: '0 auto', display: 'grid', gridTemplateColumns: '60px 200px 250px 280px', gap: 0.5, alignItems: 'start', py: 1 }}>
      {/* Number column */}
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1, minHeight: '60px' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {row?._rowNumber ? `#${row._rowNumber}` : ''}
        </Typography>
      </Box>

      {/* Address and AV/IFMV column */}
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '60px' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row?.Address || row?.address || row?.parcel_id}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          AV: {getAVUtil(caseObject) ? getAVUtil(caseObject).toLocaleString() : 'N/A'} · IFMV: ${getIFMVUtil(caseObject) ? getIFMVUtil(caseObject).toLocaleString() : 'N/A'}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
        <NumericFormat
          value={rawValue}
          onValueChange={(vals) => setRawValue(vals.value ?? '')}
          customInput={TextField}
          valueIsNumericString
          thousandSeparator
          allowNegative={false}
          isAllowed={({ floatValue, formattedValue }) => {
            if (floatValue === undefined) return true;
            if (floatValue < 0) return false;
            if (formattedValue === '-') return false;
            return true;
          }}
          onBlur={onSettlementAmountBlur}
          placeholder={isAVMode ? 'Enter AV' : 'Enter MV'}
          size="small"
          disabled={isWithdrawal || isSettled || bulkDisabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span style={{ color: isAVMode ? '#ff9800' : 'inherit', fontWeight: 'bold' }}>
                  {isAVMode ? 'AV' : '$'}
                </span>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isAVMode && roundingInfo.direction && (
                  <InputAdornment position="end" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: roundingInfo.borderColor, fontSize: '18px', fontWeight: 'bold', lineHeight: 1 }}>
                      {roundingInfo.direction === 'up' ? '↑' : roundingInfo.direction === 'down' ? '↓' : ''}
                    </Typography>
                  </InputAdornment>
                )}
                <Tooltip title={isAVMode ? 'Switch to Market Value' : 'Switch to AV'}>
                  <IconButton size="small" onClick={handleToggleMode} sx={{ p: '4px' }}>
                    <AutorenewIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            inputProps: { autoComplete: 'off' }
          }}
          sx={{
            width: 225,
            minWidth: 180,
            '& .MuiInputBase-root': {
              height: 44,
              border: withdrawalError
                ? '1px solid #d32f2f'
                : (isAVMode ? '1px solid #ff9800' : `1px solid ${roundingInfo.borderColor}`)
            },
            '& .MuiInputBase-input': {
              color: withdrawalError ? '#d32f2f' : 'inherit',
            }
          }}
        />
        {/* Summary stacked below the input */}
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {(() => {
            const n = rawValue === '' ? null : parseInt(rawValue, 10);
            if (n == null) return null;
            const propertyAssessment = getAVUtil(caseObject);
            const avFinal = applySettlementRounding(n, isAVMode, conversionFactor, rounding, propertyAssessment);
            const display = isAVMode ? `MV: $${Math.round(n / conversionFactor).toLocaleString()}` : `AV: ${avFinal.toLocaleString()}`;
            const { percent, color, text } = computeReductionPercent(n, isAVMode, propertyAssessment, getIFMVUtil(caseObject));
            return (
              <>
                {display}
                <span style={{ color: roundingInfo.borderColor, marginLeft: 4, fontWeight: 'bold', visibility: (!isAVMode && roundingInfo.direction) ? 'visible' : 'hidden' }}>
                  {roundingInfo.direction === 'up' ? '↑' : roundingInfo.direction === 'down' ? '↓' : ''}
                </span>
                {percent !== null && (
                  <span style={{ marginLeft: 4, color }}>{` (${text})`}</span>
                )}
              </>
            );
          })()}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, minHeight: '44px', display: 'flex', alignItems: 'center' }}>

        {/* WITHDRAW SECTION HERE: */}
        {/* Withdrawal Section */}
        <Box sx={{ minWidth: 0, width: '100%', position: 'relative' }}>
          {isSettled ? (
            <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              Settled
            </Typography>
          ) : (
            <>
              {/* Checkbox and reason input row - centered vertically on 44px */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', minHeight: '44px' }}>
                {/* Checkbox and label */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id={`withdrawal-${row?.parcel_id}`}
                    checked={isWithdrawal}
                    onChange={(e) => {
                      if (bulkDisabled) return;
                      setIsWithdrawal(e.target.checked);
                      if (!e.target.checked) {
                        setWithdrawalReason('');
                        setCustomWithdrawalReason('');
                        setWithdrawalError(''); // Clear error when withdrawal is unchecked
                        if (bulk?.isFirst && bulk.setWithdrawalReason && bulk.setCustomWithdrawalReason) {
                          bulk.setWithdrawalReason('');
                          bulk.setCustomWithdrawalReason('');
                        }
                      }
                      // Clear validation error when user makes changes
                      if (onClearValidationError) {
                        onClearValidationError(row?.parcel_id);
                      }
                    }}
                    disabled={bulkDisabled}
                    style={{ marginRight: 6 }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Withdrawal
                  </Typography>
                </Box>

                {/* Reason input with error text - error positioned absolutely so it doesn't affect flex alignment */}
                {isWithdrawal && (
                  <Box sx={{ position: 'relative' }}>
                    {withdrawalReason === 'Other' ? (
                      <>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Enter reason"
                          value={customWithdrawalReason}
                          onChange={(e) => {
                            if (bulkDisabled) return;
                            setCustomWithdrawalReason(e.target.value);
                            setWithdrawalError(''); // Clear error when user types
                            // Clear validation error when user makes changes
                            if (onClearValidationError) {
                              onClearValidationError(row?.parcel_id);
                            }
                            if (bulk?.isFirst && bulk.setCustomWithdrawalReason) {
                              bulk.setCustomWithdrawalReason(e.target.value);
                            }
                          }}
                          onBlur={() => {
                            if (!customWithdrawalReason.trim()) setWithdrawalReason('');
                          }}
                          sx={{ maxWidth: 160, minWidth: 160 }}
                          error={!!withdrawalError}
                          disabled={bulkDisabled}
                        />
                        {/* Error text - positioned absolutely below the input */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'error.main', 
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            mt: 0.5,
                            display: 'block',
                            minHeight: '16px',
                            visibility: withdrawalError ? 'visible' : 'hidden'
                          }}
                        >
                          {withdrawalError || '\u00A0'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <FormControl size="small" sx={{ maxWidth: 160, minWidth: 160 }}>
                          <Select
                            value={withdrawalReason}
                            onChange={(e) => {
                              if (bulkDisabled) return;
                              const value = e.target.value;
                              setWithdrawalReason(value);
                              setWithdrawalError(''); // Clear error when user selects a reason
                              if (value !== 'Other') setCustomWithdrawalReason('');
                              // Clear validation error when user makes changes
                              if (onClearValidationError) {
                                onClearValidationError(row?.parcel_id);
                              }
                              if (bulk?.isFirst && bulk.setWithdrawalReason && bulk.setCustomWithdrawalReason) {
                                bulk.setWithdrawalReason(value);
                                if (value !== 'Other') {
                                  bulk.setCustomWithdrawalReason('');
                                }
                              }
                            }}
                            displayEmpty
                            variant="outlined"
                            error={!!withdrawalError}
                            disabled={bulkDisabled}
                          >
                            <MenuItem value="" disabled>
                              Select reason
                            </MenuItem>
                            {withdrawalReasons.map((reason) => (
                              <MenuItem key={reason} value={reason}>
                                {reason}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {/* Error text - positioned absolutely below the select */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'error.main', 
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            mt: 0.5,
                            display: 'block',
                            minHeight: '16px',
                            visibility: withdrawalError ? 'visible' : 'hidden'
                          }}
                        >
                          {withdrawalError || '\u00A0'}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>


      </Box>
      {/* hidden controlled fields to be read on submit via DOM refs not needed, parent will read via callback */}
      <input type="hidden" data-parcel-id={row?.parcel_id} data-raw-value={rawValue} data-is-av={isAVMode ? '1' : '0'} data-is-withdrawal={isWithdrawal ? '1' : '0'} data-withdrawal-reason={withdrawalReason} data-custom-withdrawal-reason={customWithdrawalReason} />
    </Box>
  );
};

/**
 * MultiSettlementDialog - Handles batch settlement processing for multiple cases
 * 
 * Data Collection Approach:
 * - Uses inputMap pattern to collect data from multiple RowEditor components
 * - Each RowEditor writes its state to hidden DOM inputs with data attributes
 * - Parent component reads all hidden inputs on submit to create a map of parcel data
 * - This approach avoids complex state lifting while allowing independent row editors
 * 
 * Hidden Input Data Attributes:
 * - data-parcel-id: Unique identifier for each case
 * - data-raw-value: Current input value
 * - data-is-av: Whether input is in AV mode (1) or MV mode (0)
 * - data-is-withdrawal: Whether withdrawal is selected (1) or not (0)
 */
const MultiSettlementDialog = ({ open, onClose, rows }) => {
  const rounding = usePersistedDataStore((state) => state.settlementRounding);
  const setRounding = usePersistedDataStore((state) => state.setSettlementRounding);
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const { user } = useUser();

  const scarStageAvailable = useMemo(
    () => isScarStageAvailableForRows(rows, negotiationObj),
    [rows, negotiationObj]
  );
  const art7StageAvailable = useMemo(
    () => rows.every((row) => isMasterPetitionCase(resolveSettlementCaseItem(row, negotiationObj) || row)),
    [rows, negotiationObj]
  );

  const [stage, setStage] = useState(() => getDefaultSettlementStage(rows, negotiationObj));
  const [defaultFrom, setDefaultFrom] = useState('Aventine');
  const aventineOfferAvailable = offerSourceAvailable(rows, 'Aventine');
  const townOfferAvailable = offerSourceAvailable(rows, 'Town');
  // Outer isAV mode default for all row editors - read from persisted store
  const initialIsAVMode = usePersistedDataStore((state) => state.settlementIsAVMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Get downloadStips from persisted store to prevent stale values
  const downloadStips = usePersistedDataStore((state) => state.downloadStips);
  const setDownloadStips = usePersistedDataStore((state) => state.setDownloadStips);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [bulkType, setBulkType] = useState(null); // 'withdrawal' | 'no-change' | null
  const [bulkWithdrawalReason, setBulkWithdrawalReason] = useState('');
  const [bulkCustomWithdrawalReason, setBulkCustomWithdrawalReason] = useState('');
  const [validationErrors, setValidationErrors] = useState(new Map());
  const [focusIndex, setFocusIndex] = useState(null);

  useEffect(() => {
    if (!scarStageAvailable && stage === 'SCAR') {
      setStage(art7StageAvailable ? 'Art7' : 'BAR');
    } else if (art7StageAvailable && stage === 'BAR') {
      setStage(scarStageAvailable ? 'SCAR' : 'Art7');
    }
  }, [scarStageAvailable, art7StageAvailable, stage]);
  
  // Function to clear validation errors for a specific row
  const clearValidationError = useCallback((parcelId) => {
    setValidationErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(parcelId);
      return newMap;
    });
  }, []);

  const getConversionFactorForRow = useCallback((row) => {
    if (!row) throw new Error('[MultiSettlementDialog] Missing row.');
    return getConversionFactor(row.EQ, row.RAR);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      if (!rows || rows.length === 0) return;

      const inputs = document.querySelectorAll('input[type="hidden"][data-parcel-id]');
      const inputMap = new Map();
      inputs.forEach((el) => {
        inputMap.set(el.getAttribute('data-parcel-id'), {
          raw: el.getAttribute('data-raw-value'),
          isAV: el.getAttribute('data-is-av') === '1',
          isWithdrawal: el.getAttribute('data-is-withdrawal') === '1',
          withdrawalReason: el.getAttribute('data-withdrawal-reason'),
          customWithdrawalReason: el.getAttribute('data-custom-withdrawal-reason')
        });
      });

      const blankErrorMap = new Map();
      for (const row of rows) {
        if (isCaseSettled(row)) continue;
        const entry = inputMap.get(String(row.parcel_id));
        const rawTrimmed = entry?.raw != null ? String(entry.raw).trim() : '';
        if (!entry || rawTrimmed === '') {
          blankErrorMap.set(row.parcel_id, 'Enter a settlement value');
        }
      }

      if (blankErrorMap.size > 0) {
        setValidationErrors(blankErrorMap);
        const firstBlankIdx = rows.findIndex((r) => !isCaseSettled(r) && blankErrorMap.has(r.parcel_id));
        setFocusIndex(firstBlankIdx >= 0 ? firstBlankIdx : null);
        toast.error(
          'Enter a settlement value for each open case before settling. Offending rows are highlighted in red.',
          { autoClose: false }
        );
        setIsSubmitting(false);
        return;
      }

      const errorMap = new Map();
      let hasValidationErrors = false;

      for (const row of rows) {
        if (isCaseSettled(row)) continue;

        const entry = inputMap.get(String(row.parcel_id));
        if (entry.isWithdrawal) {
          if (isBulkAction && row !== rows[0]) continue;
          if (!entry.withdrawalReason || entry.withdrawalReason === '') {
            errorMap.set(row.parcel_id, 'Select a withdrawal reason');
            hasValidationErrors = true;
          } else if (entry.withdrawalReason === 'Other' && (!entry.customWithdrawalReason || entry.customWithdrawalReason.trim() === '')) {
            errorMap.set(row.parcel_id, 'Enter a withdrawal reason');
            hasValidationErrors = true;
          }
        }
      }

      if (hasValidationErrors) {
        setValidationErrors(errorMap);
        const firstErrIdx = rows.findIndex((r) => !isCaseSettled(r) && errorMap.has(r.parcel_id));
        setFocusIndex(firstErrIdx >= 0 ? firstErrIdx : null);
        toast.error('Please complete withdrawal details for highlighted rows.', { autoClose: false });
        setIsSubmitting(false);
        return;
      }

      // Bulk mode: only allow no-change or withdrawal on the first property
      if (isBulkAction && rows.length > 0) {
        const firstRow = rows[0];
        const firstEntry = inputMap.get(String(firstRow.parcel_id));

        if (!firstEntry.isWithdrawal) {
          const firstCase = negotiationObj?.cases?.find(c => c.parcel_id === firstRow.parcel_id) || null;
          const nFirstRaw = parseInt(String(firstEntry.raw ?? '').replace(/,/g, ''), 10);
          const nFirst = Number.isFinite(nFirstRaw)
            ? clampDisplayedSettlement(nFirstRaw, firstEntry.isAV, firstCase)
            : nFirstRaw;
          const propertyAssessmentFirst = getAVUtil(firstCase);
          const { percent } = computeReductionPercent(
            nFirst,
            firstEntry.isAV,
            propertyAssessmentFirst,
            getIFMVUtil(firstCase)
          );
          if (percent === null || Math.abs(percent) >= 0.005) {
            toast.error('Bulk Action only supports no-change or withdrawal on the first property.', { autoClose: false });
            setIsSubmitting(false);
            return;
          }
        }

        // In bulk withdrawal, use the first row's withdrawal reason for all rows.
        if (firstEntry.isWithdrawal) {
          rows.forEach((row) => {
            const entry = inputMap.get(String(row.parcel_id));
            if (entry && entry.isWithdrawal) {
              entry.withdrawalReason = firstEntry.withdrawalReason;
              entry.customWithdrawalReason = firstEntry.withdrawalReason === 'Other'
                ? firstEntry.customWithdrawalReason
                : '';
            }
          });
        }
      }

      // Prepare batch case objects
      const caseObjects = [];
      for (const row of rows) {
        if (isCaseSettled(row)) {
          continue;
        }

        const entry = inputMap.get(String(row.parcel_id));
        if (!entry) continue;

        const caseObject = negotiationObj?.cases?.find((c) => c.parcel_id === row.parcel_id);
        if (!caseObject) throw new Error('[MultiSettlementDialog] Missing case object for settlement rounding.');

        const nRaw = parseInt(String(entry.raw ?? '').replace(/,/g, ''), 10);
        if (!Number.isFinite(nRaw)) continue;
        const n = clampDisplayedSettlement(nRaw, entry.isAV, caseObject);
        const conversionFactor = getConversionFactorForRow(row);
        const currentAV = getAVUtil(caseObject);
        const finalAV = applySettlementRounding(n, entry.isAV, conversionFactor, rounding, currentAV);

        caseObjects.push({
          ...row,
          settlementValue: finalAV,
          settlementDetails: { 
            rounding, 
            isAVMode: entry.isAV, 
            conversionFactor, 
            stage,
            isWithdrawal: entry.isWithdrawal,
            withdrawalReason: entry.isWithdrawal ? (entry.withdrawalReason === 'Other' ? entry.customWithdrawalReason : entry.withdrawalReason) : ''
            // Note: downloadStips is now read directly from persisted state in settlementService
          }
        });
      }

      // Run multi-settlement validation to ensure no values violate global rules
      const violations = validateSettlementBatch(caseObjects);
      if (violations.length > 0) {
        const violationErrorMap = new Map();
        violations.forEach(({ parcel_id }) => {
          violationErrorMap.set(parcel_id, 'Settlement value outside allowed range; please adjust.');
        });
        setValidationErrors(violationErrorMap);
        const v0 = violations[0];
        const focusRowIdx = v0?.parcel_id != null ? rows.findIndex((r) => r.parcel_id === v0.parcel_id) : -1;
        setFocusIndex(focusRowIdx >= 0 ? focusRowIdx : (v0?.index ?? null));
        toast.error(
          'Settlement value(s) outside allowed range; offending rows are highlighted in red. Review and try again.',
          { autoClose: false },
        );
        setIsSubmitting(false);
        return;
      }

      if (caseObjects.length === 0) {
        toast.error('No settlements to process. All selected cases may already be settled.', { autoClose: false });
        setIsSubmitting(false);
        return;
      }

      const context = createOfferContext(user);

      // Process batch settlement
      await processSettlement(caseObjects, context);
      
      // Show success toast
      toast.success(`Successfully settled ${caseObjects.length} case${caseObjects.length === 1 ? '' : 's'}`);

      // Persist selected stage to localStorage
      try {
        const adv = JSON.parse(localStorage.getItem('advancedSettings') || '{}');
        adv.stage = stage;
        localStorage.setItem('advancedSettings', JSON.stringify(adv));
      } catch {}

      onClose?.();
    } catch (e) {
      logger.error('Error processing batch settlement:', e);
      if (!e?.isAxiosError && !e?.response) {
        toast.error(e.message || 'Error processing batch settlement', { autoClose: false });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [rows, rounding, negotiationObj, user, stage, onClose, isBulkAction, getConversionFactorForRow]);

  const getMostRecentTownResponse = useCallback((negotiations) => {
    if (!negotiations || !Array.isArray(negotiations)) return null;
    const sorted = [...negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
    for (const negotiation of sorted) {
      if (negotiation.offers && Array.isArray(negotiation.offers)) {
        const townOffer = negotiation.offers.find(o => o.OfferedBy === 'Town');
        if (townOffer && townOffer.Response) {
          return townOffer.Response;
        }
      }
    }
    return null;
  }, []);

  return (
    <StableMuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Settlements</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Rounding</Typography>
            <FormControl fullWidth size="small">
              <Select value={rounding} onChange={(e) => setRounding(e.target.value)}>
                <MenuItem value={0}>Exact</MenuItem>
                <MenuItem value={10}>Nearest 10</MenuItem>
                <MenuItem value={50}>Nearest 50</MenuItem>
                <MenuItem value={100}>Nearest 100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Stage</Typography>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined
                },
                '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined
                }
              }}
            >
              <Select value={stage} onChange={(e) => setStage(e.target.value)}>
                <MenuItem value={'SCAR'} disabled={!scarStageAvailable}>SCAR</MenuItem>
                <MenuItem value={'Art7'} disabled={!art7StageAvailable}>Art 7</MenuItem>
                <MenuItem value={'BAR'} disabled={art7StageAvailable}>BAR</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Initial Value From</Typography>
            <FormControl fullWidth size="small">
              <Select value={defaultFrom} onChange={(e) => setDefaultFrom(e.target.value)}>
                <MenuItem value={'Aventine'} disabled={!aventineOfferAvailable}>Aventine Offer</MenuItem>
                <MenuItem value={'Town'} disabled={!townOfferAvailable}>Town Offer</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, marginLeft: 'auto' }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Options</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '40px', px: 1, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="download-stips-checkbox-multi"
                  checked={downloadStips}
                  onChange={(e) => setDownloadStips(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                  Download Stips
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="bulk-action-checkbox-multi"
                  checked={isBulkAction}
                  onChange={(e) => {
                    setIsBulkAction(e.target.checked);
                    if (!e.target.checked) {
                      setBulkType(null);
                      setBulkWithdrawalReason('');
                      setBulkCustomWithdrawalReason('');
                    }
                  }}
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', mr: 0.5 }}>
                  Bulk Action
                </Typography>
                <Tooltip title="This setting means that the action applied to the first property will be applied to all.">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>


        <Box sx={{ borderTop: '1px solid #eee', pt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {rows?.map((row, index) => {
            const conversionFactor = getConversionFactorForRow(row);
            const mostRecentAventine = getMostRecentOfferValue(row?.Negotiations, 'Aventine');
            const mostRecentTown = getMostRecentOfferValue(row?.Negotiations, 'Town');
            const mostRecentTownResponse = getMostRecentTownResponse(row?.Negotiations);
            const initialFromNegotiations = offerAmount(row, defaultFrom);

            // If no offer exists, default to Workup Value (ProprietyWorkup) if available
            // Track whether the value is in MV or AV terms
            let initialValue = null;
            let isInitialValueInMV = false;

            if (initialFromNegotiations !== null) {
              // Usable offer is in AV terms. Missing, blank, and 0 are not used.
              initialValue = initialFromNegotiations;
              isInitialValueInMV = false;
            } else {
              // Fallback to Workup Value if nothing else exists
              const workupValue = row?.ProprietyWorkup;
              
              if (workupValue != null && workupValue !== undefined && workupValue > 0) {
                // Use Workup Value as-is (in MV terms, no conversion)
                initialValue = workupValue;
                isInitialValueInMV = true; // Workup Value is in MV terms
              }
            }
            
            const isSettled = isCaseSettled(row);
            
            // Non EQ/RAR: use caseObject for canonical case fields/utilities
            const caseObject = negotiationObj?.cases?.find((c) => c.parcel_id === row.parcel_id);
            if (!caseObject) throw new Error('[MultiSettlementDialog] Missing case object for row rendering.');

            // In bulk mode, default the first property's value to a no-change (equal to current)
            if (isBulkAction && index === 0 && !isSettled) {
              const assessmentAV = getAVUtil(caseObject);
              const ifmv = getIFMVUtil(caseObject);
              if (assessmentAV != null && assessmentAV !== undefined) {
                initialValue = assessmentAV;
                isInitialValueInMV = false; // AV terms
              } else if (ifmv != null && ifmv !== undefined) {
                initialValue = ifmv;
                isInitialValueInMV = true; // MV terms
              }
            }
            
            // Calculate if withdrawal should be active (for console logging)
            const shouldBeWithdrawal = defaultFrom === 'Town' && 
              mostRecentTownResponse && 
              mostRecentTownResponse.toLowerCase() === 'withdraw';
            
            // Console log all relevant information about the individual case
            logger.log('=== MultiSettlementDialog - Case Loading ===', {
              // Basic identifiers
              parcel_id: row?.parcel_id,
              id: row?.id,
              _rowNumber: row?._rowNumber,
              address: row?.Address || row?.address || row?.parcel_id,
              
              // Assessment values
              PropertyAssessment: row?.PropertyAssessment,
              AssessmentVillage: row?.AssessmentVillage,
              AssessmentAV: getAVUtil(caseObject),
              IFMV: getIFMVUtil(caseObject),
              
              // Settlement status
              isSettled,
              SCARDeterminationValue: row?.SCARDeterminationValue,
              BARDeterminationValue: row?.BARDeterminationValue,
              
              // Offer values from different sources
              OfferValue: row?.OfferValue,
              TownOffer: row?.TownOffer,
              ProprietyWorkup: row?.ProprietyWorkup,
              
              // Negotiation data
              Negotiations: row?.Negotiations,
              mostRecentAventineOffer: mostRecentAventine,
              mostRecentTownOffer: mostRecentTown,
              mostRecentTownResponse,
              
              // Default value calculation
              defaultFrom,
              initialFromNegotiations,
              initialValue,
              isInitialValueInMV,
              shouldBeWithdrawal,
              
              // Conversion and rounding settings
              conversionFactor,
              rounding,
              stage,
              initialIsAVMode,
              
              // Full row object for reference
              fullRow: row
            });
            
            return (
              <RowEditor
                key={row.id || row.parcel_id}
                row={row}
                rounding={rounding}
                stage={stage}
                conversionFactor={conversionFactor}
                defaultValue={initialValue}
                isDefaultValueInMV={isInitialValueInMV}
                isSettled={isSettled}
                onValidationError={validationErrors.get(row.parcel_id)}
                onClearValidationError={clearValidationError}
                initialIsAVMode={initialIsAVMode}
                defaultFrom={defaultFrom}
                mostRecentTownResponse={mostRecentTownResponse}
                bulk={
                  isBulkAction
                    ? {
                        isFirst: index === 0,
                        type: bulkType,
                        setType: setBulkType,
                        withdrawalReason: bulkWithdrawalReason,
                        customWithdrawalReason: bulkCustomWithdrawalReason,
                        setWithdrawalReason: setBulkWithdrawalReason,
                        setCustomWithdrawalReason: setBulkCustomWithdrawalReason
                      }
                    : null
                }
                shouldFocus={focusIndex === index}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Settling...' : 'Settle'}
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

export default React.memo(MultiSettlementDialog);


